"use client";
import React, {
  useCallback,
  useEffect,
  useId,
  useInsertionEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Avatar from "antd/es/avatar";
import Button from "antd/es/button";
import Image from "antd/es/image";
import Modal from "antd/es/modal";
import {
  AppstoreOutlined,
  CloseOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import {
  applyMemberPricing,
  formatDuration,
  formatPrice,
} from "@openings/booking-core";
import { LoadingIndicator } from "@openings/ui";
import type { LocationOpeningsItem } from "@openings/api-client";
import type { BookingOpeningsClient, SelectedService } from "../types";
import { infoPhotoStyle } from "../bookingStyles";
import { MemberTagBadges } from "../MemberTags";
import HlsVideo from "../../HlsVideo";
import { usePhotoViewTracker } from "../../usePhotoViewTracker";
import { usePhotoImpressions } from "../../usePhotoImpressions";

const MEMBER_INFO_OVERLAY_Z_INDEX = 10100;
const MEMBER_INFO_RESPONSIVE_STYLE_ID =
  "openings-member-info-responsive-styles";
const MEMBER_INFO_RESPONSIVE_STYLES = `
.member-info-modal-wrap {
  --member-info-inline-inset: 20px;
}

.member-info-modal-wrap .ant-modal-header {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.member-info-modal-wrap .ant-modal-container,
.member-info-modal-wrap .ant-modal-content {
  overflow: hidden;
  border-radius: var(
    --openings-booking-radius-sm,
    var(--openings-booking-radius, 6px)
  );
  background: var(--openings-color-surface, #fff);
}

.member-info-modal__surface {
  position: relative;
  min-height: 100%;
  color: var(--openings-color-text, #111);
  background: var(--openings-color-surface, #fff);
  font-family: var(
    --openings-booking-body-font-family,
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif
  );
}

.member-info-modal__header {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 56px;
  box-sizing: border-box;
  padding:
    var(--openings-booking-spacing-sm, 8px)
    var(--member-info-inline-inset);
  background: var(--openings-color-surface, #fff);
}

.member-info-modal__content {
  display: flex;
  flex-direction: column;
  gap: var(--openings-booking-spacing-xl, 20px);
  padding:
    var(--openings-booking-spacing-xs, 4px)
    var(--member-info-inline-inset)
    var(--openings-booking-spacing-xl, 20px);
}

.member-info-modal__content--video {
  padding-top: 200px;
}

.member-info-modal__close,
.member-info-modal__layout-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  min-width: 40px;
  height: 40px;
  min-height: 40px;
  box-sizing: border-box;
  padding: 0;
  border: 0;
  border-radius: var(--openings-booking-radius-sm, 6px);
  color: var(--openings-color-text, #111);
  background: transparent;
  cursor: pointer;
}

.member-info-modal__read-more {
  display: inline-flex;
  align-items: center;
  min-height: 40px;
  box-sizing: border-box;
  margin-top: var(--openings-booking-spacing-xs, 4px);
  padding: 0;
  border: 0;
  border-radius: var(--openings-booking-radius-sm, 6px);
  color: var(--openings-color-text, #111);
  background: transparent;
  cursor: pointer;
}

.member-info-modal__work-video {
  display: block;
  width: 100%;
  box-sizing: border-box;
  padding: 0;
  overflow: hidden;
  border: 0;
  border-radius: var(--openings-booking-radius-sm, 6px);
  background: var(--openings-color-text, #111);
  cursor: pointer;
}

.member-info-modal__close:focus-visible,
.member-info-modal__layout-toggle:focus-visible,
.member-info-modal__read-more:focus-visible,
.member-info-modal__work-video:focus-visible {
  outline: 2px solid var(--openings-color-primary, #111);
  outline-offset: 2px;
}

@media (max-width: 767px) {
  .member-info-modal-wrap {
    --member-info-inline-inset: 16px;
    padding: 0 !important;
  }

  .member-info-modal-wrap .ant-modal {
    width: 100vw !important;
    max-width: 100vw !important;
    height: 100dvh !important;
    top: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .member-info-modal-wrap .ant-modal-container,
  .member-info-modal-wrap .ant-modal-content {
    display: flex;
    flex-direction: column;
    height: 100dvh !important;
    max-height: 100dvh;
    overflow: hidden;
    border-radius: 0 !important;
  }

  .member-info-modal__header {
    min-height: calc(56px + env(safe-area-inset-top, 0px));
    padding-top:
      calc(
        var(--openings-booking-spacing-sm, 8px) +
          env(safe-area-inset-top, 0px)
      );
  }

  .member-info-modal__content {
    padding-bottom:
      calc(
        var(--openings-booking-spacing-lg, 16px) +
          env(safe-area-inset-bottom, 0px)
      );
  }

  .member-info-modal-wrap .ant-modal-body {
    flex: 1 1 auto;
    height: auto !important;
    min-height: 0;
    overflow-y: auto;
  }

  .member-info-modal__video {
    border-radius: 0 !important;
  }
}
`;

function useMemberInfoResponsiveStyles(): void {
  useInsertionEffect(() => {
    if (document.getElementById(MEMBER_INFO_RESPONSIVE_STYLE_ID)) return;

    const style = document.createElement("style");
    style.id = MEMBER_INFO_RESPONSIVE_STYLE_ID;
    style.textContent = MEMBER_INFO_RESPONSIVE_STYLES;
    document.head.appendChild(style);
  }, []);
}

function enableMutedAutoplay(
  video: HTMLVideoElement | null,
  onFirstFrame?: () => void,
): void {
  if (!video || typeof video.play !== "function") return;

  video.defaultMuted = true;
  video.muted = true;
  video.setAttribute("muted", "");

  const tryPlay = () => {
    try {
      video.play?.()?.catch?.(() => {});
    } catch {
      // jsdom's play() is not implemented
    }
  };

  tryPlay();
  video.addEventListener("canplay", tryPlay, { once: true });

  if (onFirstFrame) {
    // `timeupdate` only fires once currentTime has advanced past 0, which
    // guarantees the first frame has actually been painted — unlike `play`
    // or `ready`, which fire before any pixels are on screen.
    const reveal = () => {
      if (video.currentTime > 0) onFirstFrame();
    };
    if (typeof video.requestVideoFrameCallback === "function") {
      video.requestVideoFrameCallback(() => onFirstFrame());
    } else {
      video.addEventListener("timeupdate", reveal, { once: true });
    }
  }
}

function formatReviewDate(value?: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

/**
 * Portfolio panel for a team member. Keeps one Modal tree and lets CSS make it
 * fullscreen on mobile so hydration never swaps the dialog implementation.
 * Shows bio, profile photo, and/or work photos fetched via the openings client.
 */
export function MemberInfoModal({
  member,
  salonId,
  selectedServices = [],
  openingsClient,
  open,
  onClose,
}: {
  member: LocationOpeningsItem;
  salonId?: string;
  selectedServices?: SelectedService[];
  openingsClient: BookingOpeningsClient;
  open: boolean;
  onClose: () => void;
}) {
  useMemberInfoResponsiveStyles();
  const themeAnchorRef = useRef<HTMLSpanElement>(null);
  const getOverlayContainer = useCallback(
    () =>
      themeAnchorRef.current?.closest<HTMLElement>(
        ".openings-booking-theme-root",
      ) ?? document.body,
    [],
  );
  const serviceDetailsTitleId = useId();
  const usefulInfoTitleId = useId();

  const [photos, setPhotos] = useState<
    Array<{ workPhotoId?: string; url: string; thumbnail?: string; type?: "image" | "video" }>
  >([]);
  const [bio, setBio] = useState<string | undefined>();
  const [profilePhoto, setProfilePhoto] = useState<string | undefined>();
  const [profileVideo, setProfileVideo] = useState<
    { url?: string; thumbnail?: string } | undefined
  >(member.video ?? undefined);
  const [totalServices, setTotalServices] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [photoLayout, setPhotoLayout] = useState<"grid" | "single">("grid");
  const [reviews, setReviews] = useState<
    Array<{ note: string; customerName?: string; submittedAt?: string }>
  >([]);
  const [reviewsShown, setReviewsShown] = useState(3);
  const [headerVideoReady, setHeaderVideoReady] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const photosRef = useRef<HTMLDivElement>(null);
  const trackingEnabled = open && openingsClient.photoTrackingEnabled;
  const photoTracker = usePhotoViewTracker(trackingEnabled ? salonId : null);
  usePhotoImpressions(photosRef, trackingEnabled ? photoTracker.trackImpression : undefined, photos);
  useEffect(() => {
    if (previewOpen) photoTracker.trackOpen(photos[previewIndex]?.workPhotoId);
  }, [previewOpen, previewIndex, photos, photoTracker]);
  const memberDetailsId = member.teamMemberId ?? member.userId;
  const getLocationUserDetails = openingsClient.getLocationUserDetails;
  const canLoadMemberDetails = Boolean(
    memberDetailsId && getLocationUserDetails,
  );

  const resetMemberDetails = useCallback(() => {
    setPhotos([]);
    setBio(undefined);
    setProfilePhoto(undefined);
    setProfileVideo(member.video ?? undefined);
    setTotalServices(0);
    setReviews([]);
    setReviewsShown(3);
    setPhotoLayout("grid");
    setHeaderVideoReady(false);
    setPreviewOpen(false);
    setPreviewIndex(0);
    setLoading(canLoadMemberDetails);
  }, [canLoadMemberDetails, member.video]);

  useLayoutEffect(() => {
    if (!open) return;
    resetMemberDetails();
  }, [getLocationUserDetails, memberDetailsId, open, resetMemberDetails]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const userId = memberDetailsId;

    if (!userId || !getLocationUserDetails) {
      setLoading(false);
      return;
    }

    // The matching layout effect has already cleared the previous member and
    // entered the loading state before this request starts.
    getLocationUserDetails(userId)
      .then((res) => {
        if (!cancelled && res.ok) {
          setPhotos(res.data.photos);
          setBio(res.data.bio);
          setProfilePhoto(res.data.profilePhoto);
          setProfileVideo(res.data.profileVideo ?? member.video ?? undefined);
          setReviews(res.data.reviews ?? []);
          const stats = res.data.stats;
          setTotalServices(stats?.totalServices ?? stats?.totalHaircuts ?? 0);
        }
      })
      .catch(() => {
        setProfileVideo(member.video ?? undefined);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      }); 
    return () => {
      cancelled = true;
    };
  }, [getLocationUserDetails, memberDetailsId, open, resetMemberDetails]);

  // Full-size `src` for images so rc-image can match a clicked thumbnail to
  // its index (it compares the child's preview src against items[].src) and so
  // the lightbox shows the full asset. Videos keep the thumbnail as `src`;
  // imageRender swaps in a player.
  const previewItems = useMemo(() => {
    if (photos.length === 0) {
      return profilePhoto
        ? [{ src: profilePhoto, url: profilePhoto, type: "image" as const }]
        : [];
    }
    return photos.map((p) => ({
      src: p.type === "video" ? (p.thumbnail ?? p.url) : p.url,
      url: p.url,
      type: p.type,
    }));
  }, [photos, profilePhoto]);

  const openPreviewAt = useCallback((index: number) => {
    setPreviewIndex(index);
    setPreviewOpen(true);
  }, []);
  const selectedServiceDetails = useMemo(
    () =>
      selectedServices.map((selectedService) => {
        const resolvedService =
          applyMemberPricing([selectedService], member.services)[0] ??
          selectedService;
        const resolvedOption = resolvedService.option;
        return {
          id: selectedService.id,
          title: selectedService.title,
          description:
            resolvedOption?.description || resolvedService.description,
          price: resolvedOption?.price ?? resolvedService.price,
          duration: resolvedOption?.duration ?? resolvedService.duration,
          up: resolvedOption ? false : resolvedService.up,
        };
      }),
    [member.services, selectedServices],
  );

  const videoUrl = profileVideo?.url;
  const hasVideoHeader = Boolean(videoUrl);

  const headerBar = (
    <div className="member-info-modal__header">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--openings-booking-spacing-sm, 8px)",
        }}
      >
        <Avatar
          src={profilePhoto ?? member.photo ?? undefined}
          size={40}
          style={{ flexShrink: 0 }}
        >
          {member.name.charAt(0).toUpperCase()}
        </Avatar>
        <div style={{ minWidth: 0 }}>
          <span
            style={{
              color: "var(--openings-color-text, #111)",
              fontSize: "var(--openings-booking-font-size, 14px)",
              fontWeight: "var(--openings-booking-font-weight-semibold, 600)",
              lineHeight: 1.3,
            }}
          >
            {member.name}
          </span>
        </div>
      </div>
      <Button
        color="default"
        variant="text"
        onClick={onClose}
        icon={<CloseOutlined />}
        aria-label="Close"
        className="member-info-modal__close"
        style={{
          width: 40,
          minWidth: 40,
          height: 40,
          minHeight: 40,
          padding: 0,
        }}
      />
    </div>
  );

  const body = (
    <div className="member-info-modal__surface">
      {!loading && videoUrl ? (
        <div
          className="member-info-modal__video"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: 240,
            zIndex: 1,
            overflow: "hidden",
            borderRadius:
              "var(--openings-booking-radius-sm, 6px) var(--openings-booking-radius-sm, 6px) 0 0",
            background: "var(--openings-color-text, #111)",
          }}
        >
          <HlsVideo
            src={videoUrl}
            autoPlay
            muted
            loop
            playsInline
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
            ref={(el) => {
              enableMutedAutoplay(el, () => setHeaderVideoReady(true));
            }}
          />
          {profileVideo?.thumbnail && (
            <img
              src={profileVideo.thumbnail}
              alt=""
              aria-hidden
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                opacity: headerVideoReady ? 0 : 1,
                transition: "opacity 0.25s ease",
                pointerEvents: "none",
              }}
            />
          )}
        </div>
      ) : null}

      {headerBar}

      {loading ? (
        <div className="member-info-modal__content member-info-modal__content--loading">
          <LoadingIndicator minHeight={160} />
        </div>
      ) : (
        <div
          className={[
            "member-info-modal__content",
            hasVideoHeader ? "member-info-modal__content--video" : null,
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {bio && (
            <p
              style={{
                margin: 0,
                color: "var(--openings-color-text, #111)",
                fontSize: "var(--openings-booking-font-size, 14px)",
                lineHeight: 1.6,
              }}
            >
              {bio}
            </p>
          )}
          {selectedServiceDetails.length > 0 ? (
            <section aria-labelledby={serviceDetailsTitleId}>
              <h2
                id={serviceDetailsTitleId}
                style={{
                  margin: "0 0 8px",
                  color: "var(--openings-color-text, #111)",
                  fontSize: "var(--openings-booking-font-size, 14px)",
                  fontWeight:
                    "var(--openings-booking-font-weight-semibold, 600)",
                  lineHeight:
                    "var(--openings-booking-section-line-height, 1.35)",
                }}
              >
                Service details
              </h2>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--openings-booking-spacing-md, 12px)",
                }}
              >
                {selectedServiceDetails.map((service) => (
                  <div key={service.id}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "var(--openings-booking-spacing-md, 12px)",
                        color: "var(--openings-color-text, #111)",
                        fontSize: "var(--openings-booking-font-size, 14px)",
                        lineHeight: 1.4,
                      }}
                    >
                      <span
                        style={{
                          fontWeight:
                            "var(--openings-booking-font-weight-semibold, 600)",
                        }}
                      >
                        {service.title}
                      </span>
                      <span
                        style={{
                          flexShrink: 0,
                          fontWeight:
                            "var(--openings-booking-font-weight-medium, 500)",
                        }}
                      >
                        {typeof service.price === "number" && service.price > 0
                          ? `${formatPrice(service.price)}${service.up ? "+" : ""}`
                          : null}
                        {service.duration
                          ? `${service.price ? " · " : ""}${formatDuration(
                              service.duration,
                            )}`
                          : null}
                      </span>
                    </div>
                    {service.description ? (
                      <p
                        style={{
                          margin: "4px 0 0",
                          color: "var(--openings-color-text-secondary, #666)",
                          fontSize:
                            "var(--openings-booking-font-size-sm, 13px)",
                          lineHeight: 1.5,
                        }}
                      >
                        {service.description}
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            </section>
          ) : null}
          {totalServices > 0 && (
            <div>
              <div
                style={{
                  marginBottom: 4,
                  color: "var(--openings-color-text, #111)",
                  fontSize: "var(--openings-booking-font-size, 14px)",
                  fontWeight:
                    "var(--openings-booking-font-weight-semibold, 600)",
                  lineHeight:
                    "var(--openings-booking-section-line-height, 1.35)",
                }}
              >
                Professional stats
              </div>
              <p
                style={{
                  margin: 0,
                  color: "var(--openings-color-text-secondary, #666)",
                  fontSize: "var(--openings-booking-font-size-sm, 13px)",
                  lineHeight: 1.5,
                }}
              >
                {member.name} has served {totalServices.toLocaleString()}{" "}
                services.
              </p>
            </div>
          )}
          {/* The tags under their own heading, right before the reviews
              (Oct 21): bare pills under the name looked out of place. */}
          {member.tags?.length ? (
            <section aria-labelledby={usefulInfoTitleId}>
              <h2
                id={usefulInfoTitleId}
                style={{
                  margin: "0 0 8px",
                  color: "var(--openings-color-text, #111)",
                  fontSize: "var(--openings-booking-font-size, 14px)",
                  fontWeight:
                    "var(--openings-booking-font-weight-semibold, 600)",
                  lineHeight:
                    "var(--openings-booking-section-line-height, 1.35)",
                }}
              >
                Useful info
              </h2>
              <MemberTagBadges
                tags={member.tags}
                label={`About ${member.name}`}
              />
            </section>
          ) : null}
          {reviews.length > 0 && (
            <div>
              <div
                style={{
                  marginBottom: 8,
                  color: "var(--openings-color-text, #111)",
                  fontSize: "var(--openings-booking-font-size, 14px)",
                  fontWeight:
                    "var(--openings-booking-font-weight-semibold, 600)",
                  lineHeight:
                    "var(--openings-booking-section-line-height, 1.35)",
                }}
              >
                {/* The API returns only comments rated 5 on every item, so
                    the heading says they are selected (Issue 921). */}
                Selected 5-star reviews
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--openings-booking-spacing-sm, 8px)",
                }}
              >
                {reviews.slice(0, reviewsShown).map((review, i) => (
                  <div
                    key={`review-${i}`}
                    className="member-info-modal__review"
                    style={{
                      padding:
                        "var(--openings-booking-spacing-sm, 8px) var(--openings-booking-spacing-md, 12px)",
                      borderRadius: "var(--openings-booking-radius-sm, 6px)",
                      background:
                        "var(--openings-color-surface-hover, var(--openings-color-background, #f6f6f6))",
                    }}
                  >
                    {review.customerName && (
                      <div
                        style={{
                          fontSize:
                            "var(--openings-booking-font-size-sm, 13px)",
                          fontWeight:
                            "var(--openings-booking-font-weight-semibold, 600)",
                          lineHeight:
                            "var(--openings-booking-meta-line-height, 1.35)",
                          color: "var(--openings-color-text, #111)",
                        }}
                      >
                        {review.customerName}
                      </div>
                    )}
                    <p
                      style={{
                        margin: "4px 0",
                        fontSize: "var(--openings-booking-font-size-sm, 13px)",
                        lineHeight: 1.5,
                        color: "var(--openings-color-text, #111)",
                      }}
                    >
                      {review.note}
                    </p>
                    {review.submittedAt && (
                      <div
                        style={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <span
                          style={{
                            fontSize:
                              "var(--openings-booking-font-size-sm, 13px)",
                            lineHeight:
                              "var(--openings-booking-meta-line-height, 1.35)",
                            color: "var(--openings-color-text-secondary, #666)",
                          }}
                        >
                          {formatReviewDate(review.submittedAt)}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {reviews.length > reviewsShown && (
                <button
                  type="button"
                  className="member-info-modal__read-more"
                  onClick={() =>
                    setReviewsShown((prev) =>
                      Math.min(prev + 5, reviews.length),
                    )
                  }
                  style={{
                    fontSize: "var(--openings-booking-font-size-sm, 13px)",
                    fontWeight:
                      "var(--openings-booking-font-weight-medium, 500)",
                  }}
                >
                  Read more
                </button>
              )}
            </div>
          )}
          {(photos.length > 0 || profilePhoto) && (
            <div ref={photosRef}>
              <Image.PreviewGroup
                items={previewItems as any}
                preview={{
                  open: previewOpen,
                  current: previewIndex,
                  zIndex: MEMBER_INFO_OVERLAY_Z_INDEX + 1,
                  // Opening is driven by tile onClick (which knows the exact
                  // index) — only handle close here, so antd's stale `current`
                  // never clobbers the clicked index.
                  onOpenChange: (nextOpen) => {
                    if (!nextOpen) setPreviewOpen(false);
                  },
                  onChange: (current) => {
                    setPreviewIndex(current);
                  },
                  // Match the member page lightbox: no toolbar chrome.
                  toolbarRender: () => null,
                  imageRender: (originNode, { current }) => {
                    const item = previewItems[current];
                    if (item?.type === "video") {
                      return (
                        /* Work videos are Cloudflare Stream HLS; use HlsVideo
                           (local hls.js) — ReactPlayer pulls hls.js from a CDN
                           and fails silently. */
                        <HlsVideo
                          src={item.url ?? ""}
                          poster={
                            item.src !== item.url ? item.src : undefined
                          }
                          autoPlay
                          muted
                          loop
                          playsInline
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        />
                      );
                    }
                    return originNode;
                  },
                }}
              >
                {photos.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 8,
                    }}
                  >
                    <div
                      style={{
                        color: "var(--openings-color-text, #111)",
                        fontSize: "var(--openings-booking-font-size, 14px)",
                        fontWeight:
                          "var(--openings-booking-font-weight-semibold, 600)",
                        lineHeight:
                          "var(--openings-booking-section-line-height, 1.35)",
                      }}
                    >
                      Work Photos
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "var(--openings-booking-spacing-xs, 4px)",
                      }}
                    >
                      <button
                        type="button"
                        className="member-info-modal__layout-toggle"
                        onClick={() => setPhotoLayout("single")}
                        aria-label="Single column view"
                        aria-pressed={photoLayout === "single"}
                        style={{
                          color:
                            photoLayout === "single"
                              ? "var(--openings-color-text, #111)"
                              : "var(--openings-color-text-secondary, #666)",
                          fontSize:
                            "var(--openings-booking-font-size-lg, 16px)",
                        }}
                      >
                        <PictureOutlined />
                      </button>
                      <button
                        type="button"
                        className="member-info-modal__layout-toggle"
                        onClick={() => setPhotoLayout("grid")}
                        aria-label="Grid view"
                        aria-pressed={photoLayout === "grid"}
                        style={{
                          color:
                            photoLayout === "grid"
                              ? "var(--openings-color-text, #111)"
                              : "var(--openings-color-text-secondary, #666)",
                          fontSize:
                            "var(--openings-booking-font-size-lg, 16px)",
                        }}
                      >
                        <AppstoreOutlined />
                      </button>
                    </div>
                  </div>
                )}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      photoLayout === "single" ? "1fr" : "repeat(3, 1fr)",
                    gap: "var(--openings-booking-spacing-sm, 8px)",
                  }}
                >
                  {profilePhoto && photos.length === 0 && (
                    <Image
                      style={{
                        borderRadius: infoPhotoStyle.borderRadius,
                        objectFit: "cover",
                      }}
                      src={profilePhoto}
                      alt={`${member.name} profile`}
                      width="100%"
                      height={photoLayout === "single" ? 280 : 140}
                      onClick={() => openPreviewAt(0)}
                    />
                  )}
                  {photos.map((p, i) =>
                    p.type === "video" ? (
                      <button
                        key={i}
                        data-work-photo-id={p.workPhotoId}
                        type="button"
                        className="member-info-modal__work-video"
                        aria-label={`Open work video ${i + 1}`}
                        onClick={() => openPreviewAt(i)}
                        style={{
                          position: "relative",
                          height: photoLayout === "single" ? 280 : 140,
                        }}
                      >
                        <HlsVideo
                          src={p.url}
                          poster={p.thumbnail}
                          autoPlay
                          muted
                          loop
                          playsInline
                          ref={(el) => {
                            enableMutedAutoplay(el);
                          }}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            pointerEvents: "none",
                          }}
                        />
                      </button>
                    ) : (
                      <Image
                        key={i}
                        data-work-photo-id={p.workPhotoId}
                        style={{
                          borderRadius: infoPhotoStyle.borderRadius,
                          objectFit: "cover",
                        }}
                        height={photoLayout === "single" ? 280 : 140}
                        src={p.thumbnail ?? p.url}
                        preview={{ src: p.url }}
                        alt="Work photo"
                        width="100%"
                        onClick={() => openPreviewAt(i)}
                      />
                    ),
                  )}
                </div>
              </Image.PreviewGroup>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      <span
        ref={themeAnchorRef}
        className="member-info-modal__theme-anchor"
        aria-hidden
        hidden
      />
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        closable={false}
        title={`${member.name} profile`}
        zIndex={MEMBER_INFO_OVERLAY_Z_INDEX}
        destroyOnHidden
        wrapClassName="member-info-modal-wrap"
        getContainer={getOverlayContainer}
        style={{ top: 20 }}
        styles={{ container: { padding: 0 }, body: { padding: 0 } }}
      >
        {body}
      </Modal>
    </>
  );
}
